import { FC, memo, useMemo } from 'react';
import PlayerImage from '../../../components/PlayerImage';
import { Account } from '../../../models/Account';
import './PlayerInfo.css';
import { divisionMappings, trophyUsersS27, trophyUsersS28, trophyUsersS29, trophyUsersS30, trophyUsersS31, verifiedContentUsers } from '../../../Constants';
import { PlayerBadges, useBadges, isTopDonator as isTopDonatorBadge, isVerifiedContent as isVerifiedContentBadge, isHanErHam as isHanErHamBadge, fantasyWinnerSeasons } from '../../../services/BadgeService';
import { seasonTrophyColors } from '../../../components/Badges/FantasyWinnerBadge';
import CrownRain from '../../../components/KingEffects/CrownRain';
import KingFanfare from '../../../components/KingEffects/KingFanfare';
import { Link } from 'react-router-dom';
import { Team } from '../../../models/Team';
import DiamondOutlinedIcon from '@mui/icons-material/DiamondOutlined';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import VerifiedIcon from '@mui/icons-material/Verified';
import Tooltip from '@mui/material/Tooltip';
import steamLogo from '../../../content/SoMe/steam.png';
import faceitLogo from '../../../content/SoMe/faceit.png';
import twitterLogo from '../../../content/SoMe/twitter.png';
import twitchLogo from '../../../content/SoMe/twitch.png';
import youtubeLogo from '../../../content/SoMe/youtube.png';
import instagramLogo from '../../../content/SoMe/instagram.png';
import { RoleSummaryDto, buildRoleTooltipLines, computeDominantRole } from '../../../models/PlayerRole';

interface PlayerInfoProps {
    playerData: Account | null;
    teamData: Team | null | undefined;
    badges?: PlayerBadges;
    mapRoles?: RoleSummaryDto[];
    /**
     * Opt in to the reigning top donator's royal treatment (gold card, crown burst, fanfare).
     * PlayerInfo is shared by /player and the edit page — only the profile page
     * passes this, so the effects stay scoped to the new design.
     */
    kingEffects?: boolean;
}

const PlayerInfo: FC<PlayerInfoProps> = memo(({ playerData, teamData, badges, mapRoles, kingEffects = false }) => {
    // Hooks must be called before any early returns
    const computedRole = useMemo(() => computeDominantRole(mapRoles), [mapRoles]);

    // The player pages don't pass `badges`, so fall back to the shared page-wide badge state
    const sharedBadges = useBadges();
    const activeBadges = badges ?? sharedBadges;

    const roleTooltipContent = useMemo(() =>
        buildRoleTooltipLines(mapRoles)
            .map((l) => `${l.map}: T ${l.t} / CT ${l.ct}`)
            .join('\n'),
        [mapRoles]);

    if (!playerData) {
        return <h1 className='text-center'>Der skete en fejl</h1>;
    }

    const hasTrophy27 = trophyUsersS27.includes(playerData.steamID);
    const hasTrophy28 = trophyUsersS28.includes(playerData.steamID);
    const hasTrophy29 = trophyUsersS29.includes(playerData.steamID);
    const hasTrophy30 = trophyUsersS30.includes(playerData.steamID);
    const hasTrophy31 = trophyUsersS31.includes(playerData.steamID);

    // Badge-table only — the Constants fallback went stale (it named a former top donator) and the
    // badge is hand-assigned, so showing nothing until badges load beats showing the wrong holder.
    const isTopDonator = activeBadges ? isTopDonatorBadge(playerData.steamID, activeBadges) : false;
    const isKing = isTopDonator && kingEffects;
    // Use the badge table once it's loaded, otherwise fall back to Constants for backwards compatibility
    const isVerifiedContent = activeBadges
        ? isVerifiedContentBadge(playerData.steamID, activeBadges)
        : verifiedContentUsers.includes(playerData.steamID);
    // Badge-table only (no Constants fallback — it was added after badges moved to the backend)
    const isHanErHam = activeBadges ? isHanErHamBadge(playerData.steamID, activeBadges) : false;
    // Seasons 27-31 are still hardcoded lists below; S32 onwards come from the badge table,
    // so a season can be awarded from the admin UI without a deploy.
    const fantasySeasons = activeBadges ? fantasyWinnerSeasons(playerData.steamID, activeBadges) : [];

    // Social media links data
    const socialMediaLinks = [
        { key: 'steam', href: `https://steamcommunity.com/profiles/${playerData.steamID}/`, src: steamLogo, alt: 'Steam' },
        { key: 'faceit', href: playerData.faceitNickname ? `https://www.faceit.com/en/players/${playerData.faceitNickname}` : undefined, src: faceitLogo, alt: 'Faceit' },
        { key: 'twitter', href: playerData.twitter ? (playerData.twitter.startsWith("http") ? playerData.twitter : `https://x.com/${playerData.twitter}`) : undefined, src: twitterLogo, alt: 'Twitter' },
        { key: 'twitch', href: playerData.twitch ? `https://www.twitch.tv/${playerData.twitch}` : undefined, src: twitchLogo, alt: 'Twitch' },
        { key: 'youtube', href: playerData.youtube ? `https://www.youtube.com/@${playerData.youtube}` : undefined, src: youtubeLogo, alt: 'Youtube' },
        { key: 'instagram', href: playerData.instagram ? `https://www.instagram.com/${playerData.instagram}` : undefined, src: instagramLogo, alt: 'Instagram' },
    ].filter(link => link.href) as Array<{key: string, href: string, src: string, alt: string}>;

    return (
        <div>
            {playerData.lft && (
                <h2 className='text-center'>{playerData.playerName} søger hold!</h2>
            )}
            <div className='player-info-container'>
                <div className={`player-card${isKing ? ' player-card-king' : ''}`}>
                    <div className="player-left-section">
                        <div className="player-photo">
                            <PlayerImage id={playerData.steamID} badges={activeBadges ?? undefined} />
                        </div>
                        <h2 className="player-name">{playerData.playerName}</h2>
                    </div>

                    <div className="player-middle-section">
                        <div className="player-details">
                            {playerData.name && <div className="detail"><span>{playerData.name}</span></div>}
                            {playerData.age !== 0 && playerData.age !== null && <div className="detail"><span>{playerData.age} år</span></div>}
                            {playerData.role && <div className="detail"><span className="role-text">{playerData.role}</span></div>}
                            {computedRole && (
                                <Tooltip
                                    title={
                                        <div style={{ padding: '4px 2px' }}>
                                            <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '6px', color: '#fff' }}>Beregnet rolle</div>
                                            {roleTooltipContent.split('\n').map((line, i) => {
                                                const parts = line.split(': ');
                                                return (
                                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '0.8rem', padding: '2px 0', borderBottom: i < roleTooltipContent.split('\n').length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none' }}>
                                                        <span style={{ fontWeight: 500, color: 'rgba(255,255,255,0.9)' }}>{parts[0]}</span>
                                                        <span style={{ color: 'rgba(255,255,255,0.6)' }}>{parts[1]}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    }
                                    arrow
                                    placement="bottom"
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                bgcolor: 'rgba(30, 30, 30, 0.95)',
                                                border: '1px solid rgba(255,255,255,0.1)',
                                                borderRadius: '8px',
                                                padding: '8px 12px',
                                                maxWidth: 280,
                                                '& .MuiTooltip-arrow': {
                                                    color: 'rgba(30, 30, 30, 0.95)',
                                                },
                                            },
                                        },
                                    }}
                                >
                                    <div className="detail" style={{ cursor: 'pointer' }}>
                                        <span style={{ color: 'var(--accent-orange)', fontWeight: 600 }}>
                                            {computedRole}
                                        </span>
                                        {playerData.role && (
                                            <span style={{ opacity: 0.5, fontSize: '0.75rem', marginLeft: '4px' }}>(beregnet)</span>
                                        )}
                                    </div>
                                </Tooltip>
                            )}
                            {playerData.faceitElo !== null && <div className="detail"><span>{playerData.faceitElo} Elo</span></div>}
                        </div>
                        {/* Social media - under player info on mobile */}
                        <div className="player-social-media">
                            {socialMediaLinks.map(link => (
                                <a key={link.key} href={link.href} target="_blank" rel="noreferrer" className="social-icon">
                                    <img src={link.src} alt={link.alt} />
                                </a>
                            ))}
                            {/* Linked Discord (opt-in) — opens their profile so others can add them as a friend */}
                            {playerData.discordPublic && playerData.discordUsername && playerData.discordId && (
                                <a
                                    href={`https://discord.com/users/${playerData.discordId}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="social-icon"
                                    title={`Discord: ${playerData.discordUsername} — tilføj som ven`}
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="#5865F2" aria-hidden="true">
                                        <path d="M20.317 4.3698a19.7913 19.7913 0 0 0-4.8851-1.5152.0741.0741 0 0 0-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 0 0-.0785-.037 19.7363 19.7363 0 0 0-4.8852 1.515.0699.0699 0 0 0-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 0 0 .0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 0 0 .0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 0 0-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 0 1-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 0 1 .0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 0 1 .0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 0 1-.0066.1276 12.2986 12.2986 0 0 1-1.873.8914.0766.0766 0 0 0-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 0 0 .0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 0 0 .0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 0 0-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"/>
                                    </svg>
                                </a>
                            )}
                        </div>

                    </div>

                    {/* The reigning top donator gets the royal treatment: a burst of falling crowns
                        and a fanfare on arrival. Follows the badge, so it moves on its own whenever
                        the badge is reassigned. Scoped to /player2 via the kingEffects prop. */}
                    {isKing && <CrownRain />}
                    {isKing && <KingFanfare />}

                    <div className="player-badges">
                        {isHanErHam && (
                            <Tooltip title="Han er ham" arrow placement="top">
                                <span className="badge-icon han-er-ham">
                                    <LocalFireDepartmentIcon />
                                </span>
                            </Tooltip>
                        )}
                        {isTopDonator && (
                            <Tooltip title="Top Donator - Største støtte til PowerStats" arrow placement="top">
                                <span className="badge-icon top-donator">
                                    <WorkspacePremiumIcon />
                                </span>
                            </Tooltip>
                        )}
                        {isVerifiedContent && (
                            <Tooltip title="Verificeret Content eller Legende" arrow placement="top">
                                <span className="badge-icon verified">
                                    <VerifiedIcon />
                                </span>
                            </Tooltip>
                        )}
                        {playerData.premium && (
                            <Tooltip title="Konge - Major donator (200+ kr)" arrow placement="top">
                                <span className="badge-icon diamond">
                                    <DiamondOutlinedIcon />
                                </span>
                            </Tooltip>
                        )}
                        {playerData.donator && (
                            <Tooltip title="Donator til PowerStats" arrow placement="top">
                                <span className="badge-icon money">
                                    <MonetizationOnIcon />
                                </span>
                            </Tooltip>
                        )}
                        {hasTrophy27 && (
                            <Tooltip title="Vinder af fantasy division S27" arrow placement="top">
                                <span className="badge-icon trophy-s27">
                                    <EmojiEventsIcon fontSize="small" />
                                </span>
                            </Tooltip>
                        )}
                        {hasTrophy28 && (
                            <Tooltip title="Vinder af fantasy division S28" arrow placement="top">
                                <span className="badge-icon trophy-s28">
                                    <EmojiEventsIcon fontSize="small" />
                                </span>
                            </Tooltip>
                        )}
                        {hasTrophy29 && (
                            <Tooltip title="Vinder af fantasy division S29" arrow placement="top">
                                <span className="badge-icon trophy-s29">
                                    <EmojiEventsIcon fontSize="small" />
                                </span>
                            </Tooltip>
                        )}
                        {hasTrophy30 && (
                            <Tooltip title="Vinder af fantasy division S30" arrow placement="top">
                                <span className="badge-icon trophy-s30">
                                    <EmojiEventsIcon fontSize="small" />
                                </span>
                            </Tooltip>
                        )}
                        {hasTrophy31 && (
                            <Tooltip title="Vinder af fantasy division S31" arrow placement="top">
                                <span className="badge-icon trophy-s31">
                                    <EmojiEventsIcon fontSize="small" />
                                </span>
                            </Tooltip>
                        )}
                        {fantasySeasons.map((season) => {
                            const [from, to, ink] = seasonTrophyColors(season);
                            return (
                                <Tooltip key={season} title={`Vinder af fantasy division S${season}`} arrow placement="top">
                                    <span
                                        className="badge-icon trophy-season"
                                        style={{
                                            background: `linear-gradient(135deg, ${from}, ${to})`,
                                            color: ink,
                                            borderColor: from,
                                            boxShadow: `0 0 15px ${from}66`,
                                        }}
                                    >
                                        <EmojiEventsIcon fontSize="small" />
                                    </span>
                                </Tooltip>
                            );
                        })}
                    </div>

                    {/* Mobile team section - integrated into player card */}
                    <div className="mobile-team-section">
                        {teamData ? (
                            <Link to={`/team/${teamData.teamId}`} className="mobile-team-link">
                                <img src={teamData.teamLogo} alt="TeamLogo" className="mobile-team-logo" />
                                <div className="mobile-team-info">
                                    <span className="mobile-team-name">{teamData.teamName}</span>
                                    <span className="mobile-team-details">S{teamData.season} · {divisionMappings.get(teamData.division)}</span>
                                </div>
                            </Link>
                        ) : (
                            <div className="mobile-team-link">
                                <img src="https://www.hltv.org/img/static/team/placeholder.svg" alt="FA" className="mobile-team-logo" />
                                <div className="mobile-team-info">
                                    <span className="mobile-team-name">Free Agent</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Team card - desktop only */}
                {teamData ? (
                    <div className="player-card team-card desktop-team-card">
                        <div className="team-photo">
                            <Link to={`/team/${teamData.teamId}`}>
                                <img src={teamData.teamLogo} alt="TeamLogo" />
                            </Link>
                        </div>
                        <div className="team-content">
                            <div className="team-info">
                                <h2>{teamData.teamName}</h2>
                            </div>
                            <div className="team-details">
                                <div className="detail">
                                    <span className="detail-label">Sæson</span>
                                    <span>{teamData.season}</span>
                                </div>
                                <div className="detail">
                                    <span>{divisionMappings.get(teamData.division)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="player-card team-card desktop-team-card">
                        <div className="team-photo">
                            <img src="https://www.hltv.org/img/static/team/placeholder.svg" alt="FA" />
                        </div>
                        <div className="team-content">
                            <div className="team-info">
                                <h2>Intet hold registeret denne sæson</h2>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
});

export default PlayerInfo;